import React, { useState, useEffect } from 'react';
import {
    Container,
    Paper,
    Typography,
    Box,
    TextField,
    IconButton,
    Tooltip,
    Alert,
    Button,
    useTheme,
    Divider,
    alpha
} from '@mui/material';
import { HelpOutline } from '@mui/icons-material';
import ReactQuill from 'react-quill';
import 'react-quill/dist/quill.snow.css';
import { useDispatch, useSelector } from 'react-redux';
import { fetchGeneralJustification, updateGeneralJustification } from '../../../../store/startup/funding/fundingSlice';
import { useParams } from 'react-router-dom';

const GeneralInfoSection = () => {
    const theme = useTheme();
    const dispatch = useDispatch();
    const { startupId } = useParams();
    const { generalJustification, loading } = useSelector(state => state.funding);
    const [value, setValue] = useState('');
    const [error, setError] = useState('');

    useEffect(() => {
        dispatch(fetchGeneralJustification(startupId));
    }, [dispatch, startupId]);

    useEffect(() => {
        if (generalJustification) {
            setValue(generalJustification);
        }
    }, [generalJustification]);

    const handleChange = (newValue) => {
        setValue(newValue);
        if (error) setError('');
    };

    const isContentEmpty = (htmlContent) => {
        const textContent = htmlContent.replace(/<[^>]*>/g, '');
        return !textContent.trim();
    };

    const handleSave = async () => {
        setError('');

        if (!value || isContentEmpty(value)) {
            setError('Genel gerekçe ve temel bilgiler alanı boş bırakılamaz');
            return;
        }

        try {
            await dispatch(updateGeneralJustification({
                startupId,
                description: value
            })).unwrap();
        } catch (error) {
            console.error('Failed to update general justification:', error);
            setError('Güncelleme sırasında bir hata oluştu');
        }
    };

    const quillModules = {
        toolbar: [
            [{ 'header': [1, 2, 3, false] }],
            ['bold', 'italic', 'underline', 'strike'],
            [{ 'list': 'ordered'}, { 'list': 'bullet' }],
            ['clean']
        ]
    };

    const quillFormats = [
        'header',
        'bold', 'italic', 'underline', 'strike',
        'list', 'bullet'
    ];

    return (
        <Box sx={{ mb: 4, mt: 4 }}>

            <Divider sx={{ marginBottom: 3 }} />
            <Box sx={{ display: 'flex', width: '100%' }}>
                <Box sx={{ flexGrow: 1, width: '80%' }}>
                    <Typography variant="h5" sx={{ mb: 2 }}>
                    Genel Gerekçe ve Temel Bilgiler
                    </Typography>
                </Box>
                <Divider sx={{ marginBottom: 3 }} />
            </Box>

            <Alert
                severity="info"
                sx={{
                    mb: 3,
                    bgcolor: alpha(theme.palette.info.main, 0.08),
                    border: `1px solid ${alpha(theme.palette.info.main, 0.2)}`,
                    borderRadius: 2,
                    '& .MuiAlert-message': {
                        width: '100%'
                    }
                }}
            >
                <Box component="ul" sx={{ m: 0 }}>
                    {[
                        'Kitle fonlaması kampanyası düzenlemenin ve paya dayalı finansman modelinin tercih edilmesinin sebepleri ile girişim şirketini bu finansman yöntemini kullanmaya yönlendiren motivasyon unsurları detaylı olarak açıklanacaktır. Ayrıca, diğer olası dış finansman kaynaklarından fon sağlanamaması durumunda, bunun sebepleri de gerekçelendirilerek belirtilecektir. Bunun yanı sıra, kampanyanın süresi, varsa ek fon toplama süreci, hedeflenen toplam fon tutarı ve kampanyanın başarılı bir şekilde tamamlanması halinde ortaya çıkacak ürün ve hizmetler hakkında bilgi verilecektir. Kampanya sonucunda girişim şirketi, ilişkili taraflar ve diğer üçüncü kişiler tarafından sağlanacak faydalar da açıklanacaktır.'
                    ].map((text, index) => (
                        <Box component="li" key={index} sx={{ mb: index !== 2 ? 1 : 0 }}>
                            {text}
                        </Box>
                    ))}
                </Box>
            </Alert>

            <Box 
                elevation={2}
                sx={{ 
                    bgcolor: theme.palette.background.paper,
                    borderRadius: 2,
                    '& .quill': {
                        '& .ql-toolbar': {
                            border: `1px solid ${alpha(theme.palette.grey[500], 0.2)}`,
                            borderTopLeftRadius: theme.shape.borderRadius,
                            borderTopRightRadius: theme.shape.borderRadius,
                            backgroundColor: theme.palette.mode === 'light' 
                                ? alpha(theme.palette.grey[100], 0.5)
                                : alpha(theme.palette.grey[800], 0.5),
                            '& .ql-stroke': {
                                stroke: theme.palette.mode === 'light' 
                                    ? theme.palette.grey[700]
                                    : theme.palette.grey[300]
                            },
                            '& .ql-fill': {
                                fill: theme.palette.mode === 'light' 
                                    ? theme.palette.grey[700]
                                    : theme.palette.grey[300]
                            },
                            '& .ql-picker': {
                                color: theme.palette.text.primary
                            },
                            '& .ql-picker-options': {
                                backgroundColor: theme.palette.background.paper,
                                border: `1px solid ${alpha(theme.palette.grey[500], 0.2)}`,
                                boxShadow: theme.shadows[3]
                            }
                        },
                        '& .ql-container': {
                            border: `1px solid ${alpha(theme.palette.grey[500], 0.2)}`,
                            borderBottomLeftRadius: theme.shape.borderRadius,
                            borderBottomRightRadius: theme.shape.borderRadius,
                            backgroundColor: theme.palette.background.paper,
                            minHeight: '250px'
                        },
                        '& .ql-editor': {
                            minHeight: '250px',
                            fontFamily: theme.typography.fontFamily,
                            fontSize: theme.typography.body1.fontSize,
                            color: theme.palette.text.primary,
                            '&.ql-blank::before': {
                                color: theme.palette.text.disabled,
                                fontStyle: 'normal',
                                content: 'attr(data-placeholder)'
                            }
                        }
                    }
                }}
            >
                <ReactQuill 
                    theme="snow" 
                    value={value} 
                    onChange={handleChange}
                    modules={quillModules}
                    formats={quillFormats}
                    placeholder="Genel gerekçe ve temel bilgileri giriniz..."
                />
                {error && (
                    <Typography 
                        color="error" 
                        variant="caption" 
                        sx={{ mt: 1, display: 'block' }}
                    >
                        {error}
                    </Typography>
                )}
                
                <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 2 }}>
                <Button 
                        variant="contained"
                        onClick={handleSave}
                        disabled={loading}
                        sx={{
                            bgcolor: theme.palette.primary.main,
                            '&:hover': {
                                bgcolor: theme.palette.primary.dark,
                            },
                        }}
                    >
                        {loading ? 'Kaydediliyor...' : 'Kaydet'}
                    </Button>
                </Box>
            </Box>
        </Box>
    );
};

const FundGeneralJustification = () => {
    return (
        <div>
            <GeneralInfoSection />
        </div>
    );
};

export default FundGeneralJustification;